import { useState } from "react";
import { useSelector } from "react-redux";
import {
  selectImpersonationActive,
  selectImpersonationTarget,
} from "../../store/slices/impersonationSlice";
import useImpersonation from "../../hooks/useImpersonation";

// Unmissable "View as" preview banner (TP-View-As hard rule 2.4): rendered on
// every private page above the header, sticky so it can't be scrolled away,
// with a one-click, always-visible Exit. Presence comes from the
// impersonation slice, whose initial state reads the impersonation_origin
// localStorage key — so the banner survives a hard refresh mid-preview.
// Display data falls back to state.auth.user, which IS the target while
// previewing (restored from localStorage on boot).

// Inner component so the router hooks inside useImpersonation only run while
// a preview is active (the outer presence check needs no router context).
const ActiveImpersonationBanner = () => {
  const target = useSelector(selectImpersonationTarget);
  const authUser = useSelector((state) => state.auth?.user);
  const { exit } = useImpersonation();
  const [isExiting, setIsExiting] = useState(false);

  const name = target?.name || authUser?.name || authUser?.email || "user";
  const role =
    target?.role || authUser?.currentRole || (authUser?.roles || [])[0] || "";

  const handleExit = async () => {
    if (isExiting) return;
    setIsExiting(true);
    try {
      await exit();
    } finally {
      setIsExiting(false);
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-[100] w-full bg-amber-400 border-b border-amber-500 px-4 py-2 flex items-center justify-center gap-3 flex-wrap"
    >
      <span className="text-sm font-semibold text-black text-center">
        Viewing as {name}
        {role ? ` (${role})` : ""} — actions are performed AS this user
      </span>
      <button
        type="button"
        onClick={handleExit}
        disabled={isExiting}
        className="flex-shrink-0 px-3 py-1 text-sm font-semibold text-white bg-black rounded-md hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isExiting ? "Exiting…" : "Exit"}
      </button>
    </div>
  );
};

const ImpersonationBanner = () => {
  const active = useSelector(selectImpersonationActive);
  if (!active) return null;
  return <ActiveImpersonationBanner />;
};

export default ImpersonationBanner;
